(() => {
  const rootSelector = '[data-ori-mix-root]';
  const enhancedAttribute = 'data-ori-enhanced';
  const sectionListenerAttribute = 'data-ori-mix-section-listener';
  const introTrackSelector = '[data-ori-scroll-intro]';
  const revealUnitSelector = '[data-ori-reveal-unit]';
  const detailsSourceFrameRate = 60;
  const detailsMobileFrameRate = 24;
  const revealControllers = new Set();
  const detailsControllers = new Set();
  const mobileDetailsControllers = new Set();
  const floatingNavigationControllers = new Set();
  const hostHeaderControllers = new Set();
  let revealFrame = 0;
  let detailsFrame = 0;
  let mobileDetailsFrame = 0;
  let floatingNavigationFrame = 0;
  let hostHeaderFrame = 0;
  let revealListenersBound = false;
  let detailsListenersBound = false;
  let mobileDetailsListenersBound = false;
  let floatingNavigationListenersBound = false;
  let hostHeaderListenersBound = false;

  function clamp(value, minimum = 0, maximum = 1) {
    return Math.max(minimum, Math.min(value, maximum));
  }

  function smoothStep(value) {
    const progress = clamp(value);
    return progress * progress * (3 - (2 * progress));
  }

  function revealUnitProgress(progress, index, count, start, end) {
    if (count <= 0) return 1;
    const slot = (end - start) / count;
    const unitStart = start + (slot * index);
    const duration = Math.min(Math.max(slot * 1.6, 0.025), end - unitStart);
    return smoothStep((progress - unitStart) / duration);
  }

  function groupInlineIntroEnding(paragraph, units) {
    const configuredEnding = paragraph.getAttribute('data-ori-inline-ending')?.trim();
    if (!configuredEnding) return;

    const images = units.filter((unit) => unit.tagName === 'IMG');
    const endingImage = images[images.length - 1];
    const imageIndex = units.indexOf(endingImage);
    const endingWords = configuredEnding.split(/\s+/);
    const endingUnits = units.slice(imageIndex - endingWords.length, imageIndex);
    const matchesConfiguredEnding = endingUnits.length === endingWords.length
      && endingUnits.every((unit, index) => (
        unit.tagName !== 'IMG'
        && unit.parentElement === endingImage?.parentElement
        && unit.textContent === endingWords[index]
      ));
    if (!endingImage || !matchesConfiguredEnding) return;

    const phraseStart = endingUnits[0];

    const inlineEnding = document.createElement('span');
    inlineEnding.setAttribute('class', 'ori-mix__intro-inline-ending');
    phraseStart.before(inlineEnding);

    let sibling = phraseStart;
    while (sibling) {
      const nextSibling = sibling.nextSibling;
      inlineEnding.append(sibling);
      if (sibling === endingImage) break;
      sibling = nextSibling;
    }
  }

  function tokenizeIntro(paragraph) {
    if (!paragraph || paragraph.hasAttribute('data-ori-reveal-tokenized')) {
      return Array.from(paragraph?.querySelectorAll(revealUnitSelector) ?? []);
    }

    const walker = document.createTreeWalker(paragraph, window.NodeFilter.SHOW_TEXT);
    const textNodes = [];
    while (walker.nextNode()) {
      if (walker.currentNode.nodeValue?.trim()) textNodes.push(walker.currentNode);
    }

    textNodes.forEach((textNode) => {
      const phase = textNode.parentElement?.closest('strong') ? 'highlight' : 'body';
      const fragment = document.createDocumentFragment();
      textNode.nodeValue.split(/(\s+)/).forEach((part) => {
        if (!part) return;
        if (/^\s+$/.test(part)) {
          fragment.append(document.createTextNode(part));
          return;
        }
        const span = document.createElement('span');
        span.setAttribute('data-ori-reveal-unit', '');
        span.setAttribute('data-ori-reveal-phase', phase);
        span.textContent = part;
        fragment.append(span);
      });
      textNode.replaceWith(fragment);
    });

    paragraph.querySelectorAll('img').forEach((image) => {
      image.setAttribute('data-ori-reveal-unit', '');
      image.setAttribute('data-ori-reveal-phase', 'body');
    });
    const units = Array.from(paragraph.querySelectorAll(revealUnitSelector));
    groupInlineIntroEnding(paragraph, units);
    paragraph.setAttribute('data-ori-reveal-tokenized', 'true');
    return units;
  }

  function writeRevealProgress(controller, curtainProgress, introProgress) {
    controller.track.style.setProperty(
      '--ori-hero-curtain-progress',
      String(curtainProgress),
    );
    controller.track.style.setProperty(
      '--ori-hero-curtain-offset',
      `${-100 * curtainProgress}%`,
    );
    controller.track.style.setProperty('--ori-intro-progress', String(introProgress));

    const curtainHidden = curtainProgress >= 1;
    if (controller.curtainHidden !== curtainHidden) {
      controller.curtainHidden = curtainHidden;
      if (curtainHidden) {
        controller.curtain.setAttribute('data-ori-hero-curtain-hidden', '');
      } else {
        controller.curtain.removeAttribute('data-ori-hero-curtain-hidden');
      }
    }

    const bodyUnits = controller.units.filter((unit) => unit.getAttribute('data-ori-reveal-phase') === 'body');
    const highlightUnits = controller.units.filter((unit) => unit.getAttribute('data-ori-reveal-phase') === 'highlight');
    bodyUnits.forEach((unit, index) => unit.style.setProperty(
      '--ori-reveal-progress',
      String(revealUnitProgress(introProgress, index, bodyUnits.length, 0.10, 0.78)),
    ));
    highlightUnits.forEach((unit, index) => unit.style.setProperty(
      '--ori-reveal-progress',
      String(revealUnitProgress(introProgress, index, highlightUnits.length, 0.78, 0.94)),
    ));
  }

  function readRevealController(controller) {
    const rect = controller.track.getBoundingClientRect();
    const pin = controller.track.querySelector('.ori-mix__hero-intro-pin');
    const stableViewportHeight = Math.max(
      pin?.getBoundingClientRect().height || window.innerHeight,
      1,
    );
    if (rect.bottom <= 0 || rect.top >= stableViewportHeight) return null;
    const totalRange = Math.max(rect.height - stableViewportHeight, 1);
    const scrollDistance = clamp(-rect.top, 0, totalRange);
    const curtainRange = Math.min(stableViewportHeight, totalRange);
    const introRange = Math.max(totalRange - curtainRange, 1);
    return {
      controller,
      curtainProgress: clamp(scrollDistance / curtainRange),
      introProgress: clamp((scrollDistance - curtainRange) / introRange),
    };
  }

  function updateRevealControllers() {
    revealFrame = 0;
    const updates = [];
    revealControllers.forEach((controller) => {
      if (controller.track.isConnected === false) {
        revealControllers.delete(controller);
        return;
      }
      if (!controller.inViewport) return;
      const update = readRevealController(controller);
      if (update) updates.push(update);
    });
    updates.forEach(({ controller, curtainProgress, introProgress }) => {
      writeRevealProgress(controller, curtainProgress, introProgress);
    });
  }

  function requestRevealUpdate() {
    if (revealFrame) return;
    if (![...revealControllers].some((controller) => controller.inViewport)) return;
    revealFrame = window.requestAnimationFrame(updateRevealControllers);
  }

  function bindRevealListeners() {
    if (revealListenersBound) return;
    revealListenersBound = true;
    window.addEventListener('scroll', requestRevealUpdate, { passive: true });
    window.addEventListener('resize', requestRevealUpdate);
    window.addEventListener('orientationchange', requestRevealUpdate);
  }

  function initializeIntroReveal(root) {
    const track = root.querySelector(introTrackSelector);
    if (!track || track.hasAttribute('data-ori-scroll-observed')) return;
    track.setAttribute('data-ori-scroll-observed', 'true');
    if (reducedMotionRequested()) {
      track.setAttribute('data-ori-scroll-disabled', 'true');
      return;
    }
    const curtain = track.querySelector('[data-ori-hero-curtain]');
    const paragraph = track.querySelector('.ori-mix__intro p');
    if (!curtain || !paragraph) return;
    const units = tokenizeIntro(paragraph);
    if (!units.length) return;
    track.setAttribute('data-ori-scroll-ready', 'true');
    const controller = {
      track,
      curtain,
      curtainHidden: false,
      inViewport: typeof window.IntersectionObserver !== 'function',
      units,
    };
    revealControllers.add(controller);
    if (typeof window.IntersectionObserver === 'function') {
      controller.viewportObserver = new window.IntersectionObserver((entries) => {
        const entry = entries.find(({ target }) => target === track);
        if (!entry) return;
        controller.inViewport = Boolean(entry.isIntersecting);
        if (controller.inViewport) requestRevealUpdate();
      });
      controller.viewportObserver.observe(track);
    }
    bindRevealListeners();
    requestRevealUpdate();
  }

  function reducedMotionRequested() {
    return Boolean(
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    );
  }

  function readDetailsController(controller) {
    const rect = controller.track.getBoundingClientRect();
    const viewportHeight = Math.max(controller.pin.getBoundingClientRect().height || window.innerHeight, 1);
    if (rect.bottom <= 0 || rect.top >= viewportHeight) return null;
    const totalRange = Math.max(rect.height - viewportHeight, 1);
    const holdRange = Math.min(
      Math.max(totalRange - 1, 0),
      viewportHeight * controller.holdViewports,
    );
    const animationRange = Math.max(totalRange - holdRange, 1);
    const progress = clamp(-rect.top / animationRange);
    return { progress, started: rect.top <= 0 };
  }

  function updateDetailsCopyVisibility(controller, started) {
    if (!controller.copy) return;
    if (started) controller.copy.setAttribute('data-ori-details-copy-visible', '');
    else controller.copy.removeAttribute('data-ori-details-copy-visible');
  }

  function updateDetailsCopy(controller, progress) {
    if (!controller.copyItems.length) return;
    const activeIndex = Math.min(
      controller.copyItems.length - 1,
      Math.floor(clamp(progress) * controller.copyItems.length),
    );
    if (controller.activeCopyIndex === activeIndex) return;
    controller.copyItems.forEach((item, index) => {
      if (index === activeIndex) {
        item.setAttribute('data-ori-details-copy-active', '');
        item.setAttribute('aria-hidden', 'false');
      } else {
        item.removeAttribute('data-ori-details-copy-active');
        item.setAttribute('aria-hidden', 'true');
      }
    });
    controller.activeCopyIndex = activeIndex;
  }

  function releaseDetailsPresentedFrame(controller, frameToken) {
    if (!controller.awaitingPresentedFrame || controller.presentedFrameToken !== frameToken) return;
    controller.awaitingPresentedFrame = false;
    requestDetailsUpdate();
  }

  function stopDetailsPlayback(controller) {
    if (!controller.video.paused) controller.video.pause();
  }

  function updateDetailsActiveState(controller, active) {
    if (!controller.mobilePlayback || controller.detailsActive === active) return;
    controller.detailsActive = active;
    if (active) controller.root.setAttribute('data-ori-details-active', '');
    else controller.root.removeAttribute('data-ori-details-active');
  }

  function configureMobileDetailsTrack(controller) {
    if (!controller.mobilePlayback) return;
    const duration = Number(controller.video.duration);
    if (!Number.isFinite(duration) || duration <= 0) return;
    const viewportHeight = Math.max(
      controller.pin.getBoundingClientRect().height || window.innerHeight,
      1,
    );
    const trackHeight = Math.ceil((duration + 1) * viewportHeight);
    if (controller.mobileTrackHeight === trackHeight) return;
    controller.mobileTrackHeight = trackHeight;
    controller.track.style.setProperty('--ori-details-mobile-track-height', `${trackHeight}px`);
  }

  function updateMobileDetailsVideoPosition(controller, progress) {
    if (!controller.mobilePlayback) return;
    const framingProgress = clamp((progress - 0.25) / 0.25);
    const easedProgress = framingProgress * framingProgress * (3 - 2 * framingProgress);
    const positionX = Number((28 + easedProgress * 22).toFixed(2));
    if (controller.mobileVideoPositionX === positionX) return;
    controller.mobileVideoPositionX = positionX;
    controller.video.style.setProperty('--ori-details-mobile-video-position-x', `${positionX}%`);
  }

  function markDetailsReady(controller) {
    if (!controller.track.hasAttribute('data-ori-details-ready')) {
      controller.track.setAttribute('data-ori-details-ready', 'true');
    }
  }

  function updateDetailsControllers(timestamp = 0) {
    detailsFrame = 0;
    const updates = [];
    detailsControllers.forEach((controller) => {
      if (controller.track.isConnected === false) {
        stopDetailsPlayback(controller);
        updateDetailsActiveState(controller, false);
        detailsControllers.delete(controller);
        return;
      }
      if (!controller.inViewport) return;
      const state = readDetailsController(controller);
      if (state !== null) updates.push([controller, state]);
      else if (controller.awaitingPresentedFrame) {
        controller.presentedFrameToken += 1;
        controller.awaitingPresentedFrame = false;
      }
      if (state === null) {
        stopDetailsPlayback(controller);
        updateDetailsActiveState(controller, false);
      }
    });
    updates.forEach(([controller, { progress, started }]) => {
      updateDetailsCopyVisibility(controller, started);
      updateDetailsCopy(controller, progress);
      if (controller.mobilePlayback) updateDetailsActiveState(controller, started);
      configureMobileDetailsTrack(controller);
      updateMobileDetailsVideoPosition(controller, progress);
      const duration = Number(controller.video.duration);
      if (!Number.isFinite(duration) || duration <= 0 || controller.video.readyState < 1) return;
      const frameDuration = controller.frameDuration;
      const maximumTime = Math.max(
        0,
        duration - Math.max(frameDuration, controller.endGuardSeconds),
      );
      const targetTime = Math.min(
        maximumTime,
        Math.round((progress * duration) / frameDuration) * frameDuration,
      );
      const remainingTime = targetTime - controller.video.currentTime;
      const needsSeek = Math.abs(remainingTime) >= frameDuration / 2;
      stopDetailsPlayback(controller);
      if (controller.awaitingPresentedFrame) {
        markDetailsReady(controller);
        return;
      }
      if (!controller.video.seeking && needsSeek && timestamp - controller.lastSeekAt < controller.seekInterval) {
        requestDetailsUpdate();
        return;
      }
      if (!controller.video.seeking && needsSeek) {
        controller.lastSeekAt = timestamp;
        controller.video.currentTime = targetTime;
        if (typeof controller.video.requestVideoFrameCallback === 'function') {
          const frameToken = controller.presentedFrameToken + 1;
          controller.presentedFrameToken = frameToken;
          controller.awaitingPresentedFrame = true;
          controller.video.requestVideoFrameCallback(() => releaseDetailsPresentedFrame(controller, frameToken));
        }
      }
      markDetailsReady(controller);
    });
  }

  function requestDetailsUpdate() {
    if (detailsFrame) return;
    if (![...detailsControllers].some((controller) => controller.inViewport)) return;
    detailsFrame = window.requestAnimationFrame(updateDetailsControllers);
  }

  function bindDetailsListeners() {
    if (detailsListenersBound) return;
    detailsListenersBound = true;
    window.addEventListener('scroll', requestDetailsUpdate, { passive: true });
    window.addEventListener('resize', requestDetailsUpdate);
    window.addEventListener('orientationchange', requestDetailsUpdate);
  }

  function hasDeferredVideoSource(video) {
    return video.hasAttribute('data-src')
      || Array.from(video.querySelectorAll('source')).some((source) => source.hasAttribute('data-src'));
  }

  function hasPlayableVideoSource(video) {
    return video.hasAttribute('src')
      || Array.from(video.querySelectorAll('source')).some((source) => source.hasAttribute('src'));
  }

  function hydrateDeferredVideo(video) {
    if (video.hasAttribute('data-ori-video-hydrated')) return false;
    let attached = false;
    const videoSource = video.getAttribute('data-src');
    if (videoSource) {
      video.setAttribute('src', videoSource);
      attached = true;
    }
    const sources = Array.from(video.querySelectorAll('source'));
    const hasDeviceSpecificSources = video.hasAttribute('data-ori-details-video');
    sources.forEach((source) => {
      if (hasDeviceSpecificSources) {
        const desktopSource = source.hasAttribute('media');
        const sourceMatchesViewport = window.innerWidth >= 750 ? desktopSource : !desktopSource;
        if (!sourceMatchesViewport) {
          source.removeAttribute('src');
          return;
        }
      }
      const sourceUrl = source.getAttribute('data-src');
      if (sourceUrl) source.setAttribute('src', sourceUrl);
      if (source.hasAttribute('src')) attached = true;
    });
    if (!attached && !hasPlayableVideoSource(video)) return false;
    video.setAttribute('preload', 'auto');
    video.setAttribute('data-ori-video-hydrated', 'true');
    video.load?.();
    return true;
  }

  function mobileDeferredVideoSource(video) {
    const videoSource = video.getAttribute('data-src');
    if (videoSource) return videoSource;
    const mobileSource = Array.from(video.querySelectorAll('source')).find(
      (source) => !source.hasAttribute('media'),
    );
    return mobileSource?.getAttribute('data-src') || mobileSource?.getAttribute('src') || '';
  }

  function fullyBufferMobileVideo(video) {
    if (typeof window.fetch !== 'function' || typeof window.URL?.createObjectURL !== 'function') {
      return Promise.resolve(false);
    }
    const source = mobileDeferredVideoSource(video);
    if (!source) return Promise.resolve(false);
    return window.fetch(source, {
      cache: 'force-cache',
      credentials: 'omit',
      mode: 'cors',
      priority: 'low',
    }).then((response) => {
      if (!response.ok) throw new Error(`Unable to buffer details video: ${response.status}`);
      return response.blob();
    }).then((blob) => {
      if (video.hasAttribute('data-ori-video-hydrated')) return true;
      video.setAttribute('src', window.URL.createObjectURL(blob));
      video.setAttribute('preload', 'auto');
      video.setAttribute('data-ori-video-hydrated', 'true');
      video.setAttribute('data-ori-video-buffered', 'true');
      video.load?.();
      return true;
    }).catch(() => false);
  }

  function initializeDetailsVideo(root) {
    const video = root.querySelector('[data-ori-details-video]');
    const track = video?.closest('[data-ori-details-track]');
    const pin = track?.querySelector('.ori-mix__details-pin');
    if (!video || !track || !pin || track.hasAttribute('data-ori-details-video-observed')) return;
    if (window.innerWidth < 750 && track.hasAttribute('data-ori-details-desktop-only')) {
      track.setAttribute('data-ori-details-mobile-skipped', 'true');
      return;
    }
    track.setAttribute('data-ori-details-video-observed', 'true');
    video.pause();
    if (reducedMotionRequested()) {
      track.setAttribute('data-ori-details-video-disabled', 'true');
      return;
    }
    const mobilePlayback = window.innerWidth < 750;
    const canFullyBuffer = mobilePlayback
      && hasDeferredVideoSource(video)
      && typeof window.fetch === 'function'
      && typeof window.URL?.createObjectURL === 'function';
    let mobileBufferPromise = null;
    const ensureMobileBuffer = () => {
      if (!mobileBufferPromise) mobileBufferPromise = fullyBufferMobileVideo(video);
      return mobileBufferPromise;
    };
    if (canFullyBuffer && typeof window.IntersectionObserver === 'function') {
      const prefetchObserver = new window.IntersectionObserver((entries) => {
        const entry = entries.find(({ target }) => target === track);
        if (!entry?.isIntersecting) return;
        prefetchObserver.unobserve(track);
        ensureMobileBuffer();
      }, { rootMargin: '800% 0px' });
      prefetchObserver.observe(track);
    }
    if (video.getAttribute('preload') === 'none'
      && (hasDeferredVideoSource(video) || hasPlayableVideoSource(video))) {
      if (typeof window.IntersectionObserver === 'function') {
        const loadObserver = new window.IntersectionObserver((entries) => {
          const entry = entries.find(({ target }) => target === track);
          if (!entry?.isIntersecting) return;
          if (video.hasAttribute('data-ori-video-hydrated')) {
            loadObserver.unobserve(track);
            return;
          }
          if (canFullyBuffer) {
            loadObserver.unobserve(track);
            ensureMobileBuffer().then((buffered) => {
              if (!buffered) hydrateDeferredVideo(video);
            });
            return;
          }
          if (!hydrateDeferredVideo(video)) return;
          loadObserver.unobserve(track);
        }, { rootMargin: window.innerWidth < 750 ? '300% 0px' : '100% 0px' });
        loadObserver.observe(track);
      } else {
        if (canFullyBuffer) {
          ensureMobileBuffer().then((buffered) => {
            if (!buffered) hydrateDeferredVideo(video);
          });
        } else {
          hydrateDeferredVideo(video);
        }
      }
    }
    const copy = track.querySelector('[data-ori-details-copy]');
    const copyItems = Array.from(track.querySelectorAll('[data-ori-details-copy-item]'));
    const frameRate = mobilePlayback ? detailsMobileFrameRate : detailsSourceFrameRate;
    const controller = {
      activeCopyIndex: copyItems.findIndex((item) => item.hasAttribute('data-ori-details-copy-active')),
      awaitingPresentedFrame: false,
      copy,
      copyItems,
      detailsActive: false,
      endGuardSeconds: Math.max(
        0,
        Number(track.getAttribute('data-ori-details-end-guard-seconds')) || 0,
      ),
      holdViewports: Math.max(
        0,
        Number(track.getAttribute('data-ori-details-hold-viewports')) || 0,
      ),
      inViewport: typeof window.IntersectionObserver !== 'function',
      lastSeekAt: Number.NEGATIVE_INFINITY,
      frameDuration: 1 / frameRate,
      mobilePlayback,
      mobileTrackHeight: 0,
      mobileVideoPositionX: null,
      pin,
      presentedFrameToken: 0,
      root,
      seekInterval: 1000 / frameRate,
      track,
      video,
    };
    video.addEventListener('loadedmetadata', () => {
      configureMobileDetailsTrack(controller);
      requestDetailsUpdate();
    });
    video.addEventListener('seeked', () => {
      if (!controller.awaitingPresentedFrame) {
        requestDetailsUpdate();
        return;
      }
      const frameToken = controller.presentedFrameToken;
      window.requestAnimationFrame(() => releaseDetailsPresentedFrame(controller, frameToken));
    });
    detailsControllers.add(controller);
    if (typeof window.IntersectionObserver === 'function') {
      controller.viewportObserver = new window.IntersectionObserver((entries) => {
        const entry = entries.find(({ target }) => target === track);
        if (!entry) return;
        controller.inViewport = entry.isIntersecting;
        if (!controller.inViewport) {
          stopDetailsPlayback(controller);
          updateDetailsActiveState(controller, false);
          controller.presentedFrameToken += 1;
          controller.awaitingPresentedFrame = false;
          return;
        }
        requestDetailsUpdate();
      });
      controller.viewportObserver.observe(track);
    }
    bindDetailsListeners();
    requestDetailsUpdate();
  }

  function hydrateMobileDetailsImages(controller) {
    if (controller.imagesHydrated) return;
    controller.imagesHydrated = true;
    controller.images.forEach((image) => {
      const source = image.getAttribute('data-src');
      if (source) image.setAttribute('src', source);
    });
    controller.track.setAttribute('data-ori-mobile-details-images-ready', 'true');
  }

  function writeMobileDetailsProgress(controller, progress) {
    const lastIndex = Math.max(controller.slides.length - 1, 0);
    const position = clamp(progress) * lastIndex;
    const outgoingIndex = Math.min(Math.floor(position), lastIndex);
    const incomingIndex = Math.min(outgoingIndex + 1, lastIndex);
    const transitionProgress = outgoingIndex === lastIndex
      ? 0
      : smoothStep((position - outgoingIndex - 0.7) / 0.3);
    const activeIndex = transitionProgress < 0.5 ? outgoingIndex : incomingIndex;
    controller.slides.forEach((slide, index) => {
      let opacity = 0;
      if (controller.reducedMotion) opacity = Number(index === activeIndex);
      else if (index === outgoingIndex) opacity = 1 - transitionProgress;
      else if (index === incomingIndex) opacity = transitionProgress;
      opacity = Math.round(opacity * 1000) / 1000;
      slide.style.setProperty('--ori-mobile-details-opacity', String(opacity));
      const active = index === activeIndex;
      slide.setAttribute('aria-hidden', String(!active));
      if (active) slide.setAttribute('data-ori-mobile-details-active', '');
      else slide.removeAttribute('data-ori-mobile-details-active');
    });
  }

  function updateMobileDetailsControllers() {
    mobileDetailsFrame = 0;
    mobileDetailsControllers.forEach((controller) => {
      if (!controller.inViewport) return;
      const rect = controller.track.getBoundingClientRect();
      const viewportHeight = Math.max(
        controller.pin.getBoundingClientRect().height || window.innerHeight,
        1,
      );
      if (rect.bottom <= 0 || rect.top >= viewportHeight) return;
      const scrollRange = Math.max(rect.height - viewportHeight, 1);
      writeMobileDetailsProgress(controller, -rect.top / scrollRange);
    });
  }

  function requestMobileDetailsUpdate() {
    if (mobileDetailsFrame) return;
    if (![...mobileDetailsControllers].some((controller) => controller.inViewport)) return;
    mobileDetailsFrame = window.requestAnimationFrame(updateMobileDetailsControllers);
  }

  function bindMobileDetailsListeners() {
    if (mobileDetailsListenersBound) return;
    mobileDetailsListenersBound = true;
    window.addEventListener('scroll', requestMobileDetailsUpdate, { passive: true });
    window.addEventListener('resize', requestMobileDetailsUpdate);
    window.addEventListener('orientationchange', requestMobileDetailsUpdate);
  }

  function initializeMobileDetails(root) {
    if (window.innerWidth >= 750) return;
    const track = root.querySelector('[data-ori-mobile-details-track]');
    const pin = track?.querySelector('.ori-mix__mobile-details-pin');
    const slides = Array.from(track?.querySelectorAll('[data-ori-mobile-details-slide]') ?? []);
    if (!track || !pin || !slides.length || track.hasAttribute('data-ori-mobile-details-ready')) return;

    const controller = {
      images: Array.from(track.querySelectorAll('[data-ori-mobile-details-image]')),
      imagesHydrated: false,
      inViewport: typeof window.IntersectionObserver !== 'function',
      pin,
      reducedMotion: reducedMotionRequested(),
      root,
      slides,
      track,
    };
    track.setAttribute('data-ori-mobile-details-ready', 'true');
    writeMobileDetailsProgress(controller, 0);
    mobileDetailsControllers.add(controller);

    if (typeof window.IntersectionObserver === 'function') {
      const loadObserver = new window.IntersectionObserver((entries) => {
        const entry = entries.find(({ target }) => target === track);
        if (!entry?.isIntersecting) return;
        loadObserver.unobserve(track);
        hydrateMobileDetailsImages(controller);
      }, { rootMargin: '150% 0px' });
      loadObserver.observe(track);

      controller.viewportObserver = new window.IntersectionObserver((entries) => {
        const entry = entries.find(({ target }) => target === track);
        if (!entry) return;
        controller.inViewport = Boolean(entry.isIntersecting);
        if (controller.inViewport) controller.root.setAttribute('data-ori-details-active', '');
        else controller.root.removeAttribute('data-ori-details-active');
        if (controller.inViewport) requestMobileDetailsUpdate();
      });
      controller.viewportObserver.observe(track);
    } else {
      hydrateMobileDetailsImages(controller);
    }
    bindMobileDetailsListeners();
    requestMobileDetailsUpdate();
  }

  function controlledElement(root, id) {
    if (!id) return null;
    return Array.from(root.querySelectorAll('[id]')).find((element) => element.id === id) ?? null;
  }

  function carouselItems(carousel) {
    return Array.from(carousel.querySelectorAll('.ori-mix__carousel-track article'));
  }

  function updateCarousel(carousel, requestedIndex) {
    const items = carouselItems(carousel);
    const previous = carousel.querySelector('[data-ori-carousel-prev]');
    const next = carousel.querySelector('[data-ori-carousel-next]');
    const track = carousel.querySelector('.ori-mix__carousel-track');
    const lastIndex = items.length - 1;
    const index = Math.max(0, Math.min(requestedIndex, lastIndex));

    carousel.setAttribute('role', 'region');
    carousel.setAttribute('aria-roledescription', 'carousel');
    carousel.setAttribute('data-ori-carousel-index', String(index));
    if (track) track.setAttribute('aria-live', 'polite');

    items.forEach((item, itemIndex) => {
      const current = itemIndex === index;
      item.setAttribute('role', 'group');
      item.setAttribute('aria-roledescription', 'slide');
      item.setAttribute('aria-label', `${itemIndex + 1} of ${items.length}`);
      item.setAttribute('aria-hidden', String(!current));
      if (current) {
        item.setAttribute('aria-current', 'true');
        item.setAttribute('data-ori-carousel-current', '');
      } else {
        item.removeAttribute('aria-current');
        item.removeAttribute('data-ori-carousel-current');
      }
    });

    if (previous) previous.disabled = index <= 0;
    if (next) next.disabled = index >= lastIndex;
  }

  function initializeCarousel(carousel) {
    const track = carousel.querySelector('.ori-mix__carousel-track');
    if (track) track.setAttribute('aria-atomic', 'true');
    updateCarousel(carousel, 0);
  }

  function worldCarouselItems(track) {
    return Array.from(track.querySelectorAll('[data-ori-world-card]'));
  }

  function worldCarouselIndex(track) {
    const items = worldCarouselItems(track);
    if (!items.length) return 0;
    return items.reduce((closestIndex, item, index) => (
      Math.abs(item.offsetLeft - track.scrollLeft)
        < Math.abs(items[closestIndex].offsetLeft - track.scrollLeft)
        ? index
        : closestIndex
    ), 0);
  }

  function worldCarouselLeft(track, requestedIndex) {
    const items = worldCarouselItems(track);
    if (!items.length) return 0;
    const index = Math.max(0, Math.min(requestedIndex, items.length - 1));
    const maximumLeft = Math.max(0, track.scrollWidth - track.clientWidth);
    return Math.min(items[index].offsetLeft, maximumLeft);
  }

  function scrollWorldCarouselTo(track, requestedIndex, behavior) {
    const items = worldCarouselItems(track);
    if (!items.length) return;
    track.scrollTo({
      left: worldCarouselLeft(track, requestedIndex),
      behavior,
    });
  }

  function initializeWorldCarousel(track) {
    if (track.hasAttribute('data-ori-world-carousel-ready')) return;
    track.setAttribute('data-ori-world-carousel-ready', 'true');
    track.setAttribute('aria-roledescription', 'carousel');
    worldCarouselItems(track).forEach((item) => {
      item.setAttribute('role', 'group');
      item.setAttribute('aria-roledescription', 'slide');
    });

    const scrollBehavior = () => reducedMotionRequested() ? 'auto' : 'smooth';
    const next = track.parentElement?.querySelector('[data-ori-world-next]');
    let drag = null;

    next?.addEventListener('click', () => {
      scrollWorldCarouselTo(track, worldCarouselIndex(track) + 1, scrollBehavior());
    });

    const applyDragPosition = (activeDrag) => {
      const desired = activeDrag.originScrollLeft
        + (activeDrag.originX - activeDrag.currentX) * activeDrag.pixelRatio;
      const maximumLeft = Math.max(0, track.scrollWidth - track.clientWidth);
      track.scrollLeft = Math.max(0, Math.min(desired, maximumLeft));
      activeDrag.framePending = false;
    };

    track.addEventListener('keydown', (event) => {
      const currentIndex = worldCarouselIndex(track);
      let requestedIndex;
      if (event.key === 'ArrowLeft') requestedIndex = currentIndex - 1;
      else if (event.key === 'ArrowRight') requestedIndex = currentIndex + 1;
      else if (event.key === 'Home') requestedIndex = 0;
      else if (event.key === 'End') requestedIndex = worldCarouselItems(track).length - 1;
      else return;

      event.preventDefault();
      scrollWorldCarouselTo(track, requestedIndex, scrollBehavior());
    });

    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      const visualWidth = track.getBoundingClientRect().width;
      track.setAttribute('data-ori-world-pointer-active', '');
      drag = {
        activated: false,
        framePending: false,
        pointerId: event.pointerId,
        startX: event.clientX,
        currentX: event.clientX,
        originX: event.clientX,
        originScrollLeft: track.scrollLeft,
        pixelRatio: visualWidth > 0 ? track.clientWidth / visualWidth : 1,
      };
      track.setPointerCapture(event.pointerId);
    });

    track.addEventListener('pointermove', (event) => {
      if (!drag || event.pointerId !== drag.pointerId) return;
      drag.currentX = event.clientX;
      if (!drag.activated) {
        if (Math.abs(drag.startX - event.clientX) < 8) return;
        drag.activated = true;
        drag.originX = event.clientX;
        drag.originScrollLeft = track.scrollLeft;
        track.setAttribute('data-ori-world-dragging', '');
      }
      if (!drag.framePending) {
        drag.framePending = true;
        const activeDrag = drag;
        window.requestAnimationFrame(() => {
          if (drag === activeDrag) applyDragPosition(activeDrag);
        });
      }
      event.preventDefault();
    });

    const finishDrag = (event) => {
      if (!drag || event.pointerId !== drag.pointerId) return;
      const completedDrag = drag;
      completedDrag.currentX = Number.isFinite(event.clientX) ? event.clientX : completedDrag.currentX;
      if (completedDrag.activated) applyDragPosition(completedDrag);
      if (track.hasPointerCapture(event.pointerId)) track.releasePointerCapture(event.pointerId);
      track.removeAttribute('data-ori-world-dragging');
      track.removeAttribute('data-ori-world-pointer-active');
      drag = null;
    };

    track.addEventListener('pointerup', finishDrag);
    track.addEventListener('pointercancel', finishDrag);
  }

  function initializeAutoRail(viewport) {
    if (viewport.hasAttribute('data-ori-auto-rail-ready')) return;
    viewport.setAttribute('data-ori-auto-rail-ready', 'true');
    if (reducedMotionRequested()) {
      viewport.setAttribute('data-ori-auto-rail-disabled', 'true');
      return;
    }

    const track = viewport.querySelector('[data-ori-auto-rail-track]');
    const sequence = track?.querySelector('[data-ori-auto-rail-sequence]');
    if (!track || !sequence) return;

    const clone = sequence.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('data-ori-auto-rail-clone', '');
    track.append(clone);

    const speed = 30;
    const pauseReasons = new Set();
    let frame = 0;
  let lastTimestamp = null;
  let offset = 0;
  let visible = !window.IntersectionObserver;
  let hoverArmed = false;

    const updatePausedState = () => {
      if (pauseReasons.size) viewport.setAttribute('data-ori-auto-rail-paused', '');
      else viewport.removeAttribute('data-ori-auto-rail-paused');
    };

    const pause = (reason) => {
      pauseReasons.add(reason);
      updatePausedState();
    };

    const resume = (reason) => {
      pauseReasons.delete(reason);
      updatePausedState();
    };

    const tick = (timestamp) => {
      frame = 0;
      if (viewport.isConnected === false || !visible) {
        lastTimestamp = null;
        return;
      }

      if (lastTimestamp === null) lastTimestamp = timestamp;
      const elapsed = Math.min(Math.max(timestamp - lastTimestamp, 0), 64);
      lastTimestamp = timestamp;
      const distance = sequence.getBoundingClientRect().width;
      if (!pauseReasons.size && distance > 0) {
        offset = (offset + ((elapsed / 1000) * speed)) % distance;
        track.style.setProperty('--ori-auto-rail-offset', `${-offset}px`);
      }
      frame = window.requestAnimationFrame(tick);
    };

    const start = () => {
      if (!frame && visible) frame = window.requestAnimationFrame(tick);
    };

  viewport.addEventListener('pointerenter', (event) => {
    if (event.pointerType === 'mouse') hoverArmed = true;
  });
  viewport.addEventListener('pointermove', (event) => {
    if (event.pointerType !== 'mouse' || !hoverArmed) return;
    hoverArmed = false;
    pause('hover');
  });
  viewport.addEventListener('pointerleave', (event) => {
    if (event.pointerType !== 'mouse') return;
    hoverArmed = false;
    resume('hover');
  });
    viewport.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      pause(`pointer-${event.pointerId}`);
    });
    const releasePointer = (event) => resume(`pointer-${event.pointerId}`);
    viewport.addEventListener('pointerup', releasePointer);
    viewport.addEventListener('pointercancel', releasePointer);
    viewport.addEventListener('focusin', () => pause('focus'));
    viewport.addEventListener('focusout', () => resume('focus'));

    if (window.IntersectionObserver) {
      const observer = new window.IntersectionObserver((entries) => {
        visible = Boolean(entries[0]?.isIntersecting);
        lastTimestamp = null;
        start();
      });
      observer.observe(viewport);
    }
    start();
  }

  function initializePreparePlayer(player) {
    if (player.hasAttribute('data-ori-prepare-player-ready')) return;
    player.setAttribute('data-ori-prepare-player-ready', 'true');
    const prepare = player.closest('.ori-mix__prepare');
    const steps = Array.from(prepare?.querySelectorAll('[data-ori-prepare-step]') ?? []);
    const heading = prepare?.querySelector('[data-ori-prepare-heading]');
    if (!heading || !steps.length) return;
    player.muted = true;
    player.defaultMuted = true;
    player.playsInline = true;
    player.setAttribute('autoplay', '');
    player.setAttribute('webkit-playsinline', '');
    let prepareVisible = typeof window.IntersectionObserver !== 'function';

    const playPlayer = () => {
      if (!player.hasAttribute('src')) {
        const activeStep = steps.find((step) => step.hasAttribute('data-ori-prepare-active')) ?? steps[0];
        const source = activeStep?.getAttribute('data-ori-prepare-video');
        if (!source) return;
        player.setAttribute('src', source);
        player.setAttribute('preload', 'auto');
        player.currentTime = 0;
        player.load?.();
      }
      const playback = player.play?.();
      playback?.catch?.(() => {});
    };

    const updateActiveStep = (activeStep) => {
      steps.forEach((step) => {
        const selected = step === activeStep;
        step.setAttribute('aria-selected', String(selected));
        if (selected) {
          step.setAttribute('data-ori-prepare-active', '');
          step.setAttribute('aria-current', 'step');
        } else {
          step.removeAttribute('data-ori-prepare-active');
          step.removeAttribute('aria-current');
        }
      });
      heading.textContent = activeStep.textContent?.trim() ?? '';
    };

    const playStep = (step) => {
      const source = step.getAttribute('data-ori-prepare-video');
      if (!source) return;
      prepare?.removeAttribute('data-ori-prepare-playing');
      if (player.getAttribute('src') !== source) {
        player.setAttribute('src', source);
        player.setAttribute('preload', 'auto');
        player.currentTime = 0;
        player.load?.();
      } else {
        player.currentTime = 0;
      }
      player.setAttribute('aria-label', `ORI Mix ${step.textContent?.trim() ?? ''} demonstration`);
      updateActiveStep(step);
      playPlayer();
    };

    steps.forEach((step) => {
      step.addEventListener('click', () => playStep(step));
    });
    player.addEventListener('playing', () => prepare?.setAttribute('data-ori-prepare-playing', ''));
    player.addEventListener('canplay', () => {
      if (prepareVisible && player.paused) playPlayer();
    });
    ['pause', 'error', 'emptied'].forEach((eventName) => {
      player.addEventListener(eventName, () => prepare?.removeAttribute('data-ori-prepare-playing'));
    });
    if (prepare && typeof window.IntersectionObserver === 'function') {
      const observer = new window.IntersectionObserver((entries) => {
        const visible = Boolean(entries[0]?.isIntersecting);
        if (!visible) {
          prepareVisible = false;
          player.pause?.();
          return;
        }
        if (prepareVisible) return;
        prepareVisible = true;
        playPlayer();
      }, { rootMargin: '200px 0px' });
      observer.observe(prepare);
    }
    updateActiveStep(steps.find((step) => step.hasAttribute('data-ori-prepare-active')) ?? steps[0]);
  }

  function initializeTabs(container, options) {
    const {
      readyAttribute,
      tabSelector,
      panelSelector,
    } = options;
    if (container.hasAttribute(readyAttribute)) return;
    container.setAttribute(readyAttribute, 'true');
    const tabs = Array.from(container.querySelectorAll(tabSelector));
    const panels = Array.from(container.querySelectorAll(panelSelector));

    const selectTab = (selectedTab) => {
      if (selectedTab.getAttribute('aria-selected') === 'true') return;
      const panelId = selectedTab.getAttribute('aria-controls');
      tabs.forEach((tab) => {
        const selected = tab === selectedTab;
        tab.setAttribute('aria-selected', String(selected));
        tab.setAttribute('tabindex', selected ? '0' : '-1');
      });
      panels.forEach((panel) => {
        const selected = panel.id === panelId;
        panel.hidden = !selected;
        panel.removeAttribute('data-ori-panel-entering');
        if (selected && !reducedMotionRequested()) {
          panel.setAttribute('data-ori-panel-entering', '');
          panel.addEventListener('animationend', () => {
            panel.removeAttribute('data-ori-panel-entering');
          }, { once: true });
        }
      });
    };

    tabs.forEach((tab) => tab.addEventListener('click', () => selectTab(tab)));
  }

  function initializeMotionTabs(motion) {
    initializeTabs(motion, {
      readyAttribute: 'data-ori-motion-tabs-ready',
      tabSelector: '[data-ori-motion-tab]',
      panelSelector: '[data-ori-motion-panel]',
    });
  }

  function initializeFlavorTabs(flavor) {
    initializeTabs(flavor, {
      readyAttribute: 'data-ori-flavor-tabs-ready',
      tabSelector: '[data-ori-flavor-tab]',
      panelSelector: '[data-ori-flavor-panel]',
    });
  }

  function initializeSpecificationTabs(specifications) {
    initializeTabs(specifications, {
      readyAttribute: 'data-ori-spec-tabs-ready',
      tabSelector: '[data-ori-spec-tab]',
      panelSelector: '[data-ori-spec-panel]',
    });
  }

  function setFaqPanelState(panel, expanded) {
    panel.hidden = false;
    panel.setAttribute('aria-hidden', String(!expanded));
    if (expanded) panel.setAttribute('data-ori-faq-open', '');
    else panel.removeAttribute('data-ori-faq-open');
  }

  function initializeFaq(root) {
    root.querySelectorAll('[data-ori-faq-trigger]').forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
      const panel = controlledElement(root, trigger.getAttribute('aria-controls'));
      if (panel) setFaqPanelState(panel, false);
    });
  }

  function initializeFaqCategories(root) {
    root.querySelectorAll('[data-ori-faq-categories]').forEach((categories) => {
      if (categories.hasAttribute('data-ori-faq-categories-ready')) return;
      categories.setAttribute('data-ori-faq-categories-ready', 'true');
      const buttons = Array.from(categories.querySelectorAll('[data-ori-faq-category-button]'));
      const items = Array.from(root.querySelectorAll('[data-ori-faq-category]'));
      if (!buttons.length || !items.length) return;

      const selectCategory = (selectedButton) => {
        const selectedCategory = selectedButton.getAttribute('data-ori-faq-category-button');
        buttons.forEach((button) => {
          button.setAttribute('aria-pressed', String(button === selectedButton));
        });
        items.forEach((item) => {
          const filtered = item.getAttribute('data-ori-faq-category') !== selectedCategory;
          if (filtered) item.setAttribute('data-ori-faq-filtered', '');
          else item.removeAttribute('data-ori-faq-filtered');
        });
      };

      buttons.forEach((button) => button.addEventListener('click', () => selectCategory(button)));
      selectCategory(buttons.find((button) => button.getAttribute('aria-pressed') === 'true') ?? buttons[0]);
    });
  }

  function initializeRevealGroups(root) {
    const groups = Array.from(root.querySelectorAll('[data-ori-reveal]'));
    if (!groups.length) return;

    const reveal = (group) => group.setAttribute('data-ori-revealed', '');
    if (reducedMotionRequested() || typeof window.IntersectionObserver !== 'function') {
      groups.forEach(reveal);
      return;
    }

    const observeGroups = () => {
      const observer = new window.IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          reveal(entry.target);
          observer.unobserve(entry.target);
        });
      }, {
        threshold: 0.16,
        rootMargin: '0px 0px -8% 0px',
      });

      groups.forEach((group) => observer.observe(group));
    };

    /* Let the hidden starting state reach the screen before an in-view group
       can be revealed. Without this painted frame, the browser collapses both
       states into the first render and there is no visible fade-in. */
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(observeGroups);
    });
  }

  function fitScreen(screen) {
    if (!screen) return;
    const stage = screen.querySelector('.ori-mix__screen-stage');
    if (!stage) return;

    const designHeight = Number.parseFloat(screen.getAttribute('data-design-height') ?? '');
    if (!Number.isFinite(designHeight) || designHeight <= 0) return;

    screen.style.setProperty('--ori-design-height', `${designHeight}px`);
    const fullBleed = screen.classList.contains('ori-mix__screen--hero')
      || screen.classList.contains('ori-mix__screen--prepare');
    let scale = 1;

    if (!fullBleed && window.innerWidth > 1024) {
      const desktopDesignWidth = 1470;
      const desktopGutter = 36;
      const availableWidth = Math.max(window.innerWidth - (desktopGutter * 2), 1);
      scale = Math.min(
        availableWidth / desktopDesignWidth,
        window.innerHeight / designHeight,
      );
    } else if (!fullBleed) {
      const naturalHeight = Math.max(
        stage.scrollHeight || 0,
        stage.getBoundingClientRect().height || 0,
        1,
      );
      const stableViewportHeight = Math.max(
        screen.getBoundingClientRect().height || 0,
        1,
      );
      scale = Math.min(1, stableViewportHeight / naturalHeight);
    }

    const screenWidth = Math.max(
      screen.getBoundingClientRect().width || window.innerWidth || 0,
      1,
    );
    const inlineBleed = !fullBleed && window.innerWidth <= 749
      ? Math.max(((screenWidth / Math.max(scale, Number.EPSILON)) - screenWidth) / 2, 0)
      : 0;

    const requestedNavigationOffset = 42;
    let navigationOffset = 0;

    if (!fullBleed && window.innerWidth > 1024) {
      const scaledHeight = designHeight * scale;
      const availableStageInset = Math.max(window.innerHeight - scaledHeight, 0) / 2;
      const unscaledStageInset = availableStageInset / Math.max(scale, Number.EPSILON);
      navigationOffset = Math.min(requestedNavigationOffset, unscaledStageInset);
    }

    screen.style.setProperty('--ori-anchor-nav-offset', `${navigationOffset}px`);
    stage.style.setProperty('--ori-screen-scale', String(scale));
    stage.style.setProperty('--ori-screen-inline-bleed', `${inlineBleed}px`);
    const screenName = screen.getAttribute('data-ori-screen');
    const compactScreen = screenName === 'faq' || screenName === 'capsules';
    if (compactScreen && window.innerWidth > 1024) {
      screen.style.setProperty('height', `${designHeight * scale}px`);
      stage.style.setProperty('top', '0px');
      stage.style.setProperty('transform-origin', 'top center');
      stage.style.setProperty('transform', `translate(-50%, 0) scale(${scale})`);
    } else if (compactScreen) {
      screen.style.removeProperty('height');
      stage.style.removeProperty('top');
      stage.style.removeProperty('transform-origin');
      stage.style.removeProperty('transform');
    }
    screen.setAttribute('data-ori-screen-fitted', 'true');
  }

  function fitRootScreens(root) {
    root.querySelectorAll('[data-ori-screen]').forEach(fitScreen);
  }

  function updateMobileViewportHeight(root) {
    if (window.innerWidth >= 750) {
      root.style.setProperty('--ori-mobile-viewport-height', '');
      return;
    }
    const viewportHeight = Math.max(Math.round(window.innerHeight || 0), 1);
    root.style.setProperty('--ori-mobile-viewport-height', `${viewportHeight}px`);
  }

  function keepFaqItemVisible(trigger, panel) {
    const list = trigger.closest('.ori-mix__faq-list');
    const item = trigger.closest('.ori-mix__faq-item');
    if (!list || !item || typeof item.scrollIntoView !== 'function') return;
    const scrollItemIntoView = () => {
      const listRect = list.getBoundingClientRect();
      const itemRect = item.getBoundingClientRect();
      if (itemRect.top >= listRect.top && itemRect.bottom <= listRect.bottom) return;
      item.scrollIntoView({
        behavior: reducedMotionRequested() ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'nearest',
      });
    };

    scrollItemIntoView();
    if (!panel || reducedMotionRequested()) return;
    panel.addEventListener('transitionend', () => {
      if (trigger.getAttribute('aria-expanded') === 'true') scrollItemIntoView();
    }, { once: true });
  }

  function initializeScreenFitting(root) {
    updateMobileViewportHeight(root);
    const screens = Array.from(root.querySelectorAll('[data-ori-screen]'));
    const observedStages = [];
    screens.forEach((screen) => {
      if (screen.hasAttribute('data-ori-screen-observed')) {
        fitScreen(screen);
        return;
      }
      screen.setAttribute('data-ori-screen-observed', 'true');
      fitScreen(screen);
      const stage = screen.querySelector('.ori-mix__screen-stage');
      if (stage) observedStages.push(stage);
    });

    if (observedStages.length && window.ResizeObserver) {
      const observer = new window.ResizeObserver((entries) => {
        const resizedScreens = new Set();
        entries.forEach((entry) => {
          const screen = entry.target.closest?.('[data-ori-screen]');
          if (screen) resizedScreens.add(screen);
        });
        resizedScreens.forEach(fitScreen);
      });
      observedStages.forEach((stage) => observer.observe(stage));
    }

    const refit = () => fitRootScreens(root);
    const refitForOrientation = () => {
      updateMobileViewportHeight(root);
      fitRootScreens(root);
    };
    window.addEventListener('resize', refit);
    window.addEventListener('orientationchange', refitForOrientation);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(refit);
  }

  function handleRootClick(root, event) {
    const faqTrigger = event.target.closest('[data-ori-faq-trigger]');
    if (faqTrigger && root.contains(faqTrigger)) {
      const panel = controlledElement(root, faqTrigger.getAttribute('aria-controls'));
      if (!panel) return;
      const expanded = faqTrigger.getAttribute('aria-expanded') !== 'true';
      if (expanded) {
        root.querySelectorAll('[data-ori-faq-trigger]').forEach((trigger) => {
          if (trigger === faqTrigger) return;
          trigger.setAttribute('aria-expanded', 'false');
          const otherPanel = controlledElement(root, trigger.getAttribute('aria-controls'));
          if (otherPanel) setFaqPanelState(otherPanel, false);
        });
      }
      faqTrigger.setAttribute('aria-expanded', String(expanded));
      setFaqPanelState(panel, expanded);
      fitScreen(faqTrigger.closest('[data-ori-screen]'));
      if (expanded) keepFaqItemVisible(faqTrigger, panel);
      return;
    }

    const previous = event.target.closest('[data-ori-carousel-prev]');
    const next = event.target.closest('[data-ori-carousel-next]');
    const control = previous ?? next;
    if (control && root.contains(control)) {
      const carousel = control.closest('[data-ori-carousel]');
      if (!carousel || control.disabled) return;
      const currentIndex = Number.parseInt(carousel.getAttribute('data-ori-carousel-index') ?? '0', 10);
      updateCarousel(carousel, currentIndex + (next ? 1 : -1));
      return;
    }

    const anchor = event.target.closest('a[href^="#"]');
    if (!anchor || !root.contains(anchor)) return;
    const href = anchor.getAttribute('href');
    const target = href && href.length > 1 ? controlledElement(root, href.slice(1)) : null;
    if (!target) return;

    event.preventDefault();
    target.scrollIntoView({
      behavior: reducedMotionRequested() ? 'auto' : 'smooth',
      block: 'start',
    });
  }

  function setFloatingNavigationVisible(controller, visible) {
    if (controller.visible === visible) return;

    controller.visible = visible;

    if (visible) {
      controller.floating.setAttribute('data-ori-floating-visible', '');
      controller.floating.setAttribute('aria-hidden', 'false');
      controller.floating.removeAttribute('inert');
      controller.source.setAttribute('aria-hidden', 'true');
      controller.source.setAttribute('inert', '');
      return;
    }

    controller.floating.removeAttribute('data-ori-floating-visible');
    controller.floating.setAttribute('aria-hidden', 'true');
    controller.floating.setAttribute('inert', '');

    if (controller.sourceAriaHidden === null) {
      controller.source.removeAttribute('aria-hidden');
    } else {
      controller.source.setAttribute('aria-hidden', controller.sourceAriaHidden);
    }

    if (controller.sourceInert === null) {
      controller.source.removeAttribute('inert');
    } else {
      controller.source.setAttribute('inert', controller.sourceInert);
    }
  }

  function readFloatingNavigation(controller) {
    const sourceRect = controller.source.getBoundingClientRect();
    const rootRect = controller.root.getBoundingClientRect();
    const floatingRect = controller.floating.getBoundingClientRect();
    const topInset = 16;
    const floatingHeight = floatingRect.height || 60;

    return sourceRect.bottom <= 0 && rootRect.bottom > topInset + floatingHeight;
  }

  function updateFloatingNavigations() {
    floatingNavigationFrame = 0;
    const updates = [];

    floatingNavigationControllers.forEach((controller) => {
      if (!controller.root.isConnected) {
        floatingNavigationControllers.delete(controller);
        return;
      }

      updates.push([controller, readFloatingNavigation(controller)]);
    });

    updates.forEach(([controller, visible]) => {
      setFloatingNavigationVisible(controller, visible);
    });
  }

  function requestFloatingNavigationUpdate() {
    if (floatingNavigationFrame) return;
    floatingNavigationFrame = window.requestAnimationFrame(updateFloatingNavigations);
  }

  function bindFloatingNavigationListeners() {
    if (floatingNavigationListenersBound) return;
    floatingNavigationListenersBound = true;

    window.addEventListener('scroll', requestFloatingNavigationUpdate, { passive: true });
    window.addEventListener('resize', requestFloatingNavigationUpdate);
    window.addEventListener('orientationchange', requestFloatingNavigationUpdate);
  }

  function initializeFloatingNavigation(root) {
    if (root.hasAttribute('data-ori-floating-nav-ready')) return;

    const source = Array.from(root.querySelectorAll('.ori-mix__anchor-nav')).find(
      (navigation) => !navigation.hasAttribute('data-ori-floating-nav')
    );

    if (!source) return;

    const floating = source.cloneNode(true);
    const sourceClass = source.getAttribute('class') || '';

    floating.setAttribute(
      'class',
      `${sourceClass} ori-mix__anchor-nav--floating`.trim()
    );
    floating.setAttribute('data-ori-floating-nav', '');
    floating.setAttribute('aria-hidden', 'true');
    floating.setAttribute('inert', '');
    root.append(floating);

    floatingNavigationControllers.add({
      root,
      source,
      floating,
      visible: false,
      sourceAriaHidden: source.getAttribute('aria-hidden'),
      sourceInert: source.getAttribute('inert'),
    });

    root.setAttribute('data-ori-floating-nav-ready', 'true');
    bindFloatingNavigationListeners();
    requestFloatingNavigationUpdate();
  }

  function readHostHeaderController(controller) {
    const headerHeight = controller.header.getBoundingClientRect().height || 0;
    const triggerRect = controller.trigger.getBoundingClientRect();
    const rootRect = controller.root.getBoundingClientRect();
    return triggerRect.top <= headerHeight && rootRect.bottom > headerHeight;
  }

  function updateHostHeaders() {
    hostHeaderFrame = 0;
    const headerStates = new Map();

    hostHeaderControllers.forEach((controller) => {
      if (!controller.root.isConnected || !controller.trigger.isConnected || !controller.header.isConnected) {
        controller.header.removeAttribute('data-ori-host-header-hidden');
        hostHeaderControllers.delete(controller);
        return;
      }

      const hidden = readHostHeaderController(controller);
      headerStates.set(controller.header, Boolean(headerStates.get(controller.header)) || hidden);
    });

    headerStates.forEach((hidden, header) => {
      if (hidden) header.setAttribute('data-ori-host-header-hidden', '');
      else header.removeAttribute('data-ori-host-header-hidden');
    });
  }

  function requestHostHeaderUpdate() {
    if (hostHeaderFrame) return;
    hostHeaderFrame = window.requestAnimationFrame(updateHostHeaders);
  }

  function bindHostHeaderListeners() {
    if (hostHeaderListenersBound) return;
    hostHeaderListenersBound = true;
    window.addEventListener('scroll', requestHostHeaderUpdate, { passive: true });
    window.addEventListener('resize', requestHostHeaderUpdate);
    window.addEventListener('orientationchange', requestHostHeaderUpdate);
  }

  function initializeHostHeaderVisibility(root) {
    if (root.hasAttribute('data-ori-host-header-ready')) return;
    const trigger = root.querySelector('[data-ori-host-header-trigger]');
    const header = document.querySelectorAll('.section-header')[0];
    if (!trigger || !header) return;

    hostHeaderControllers.add({ root, trigger, header });
    root.setAttribute('data-ori-host-header-ready', 'true');
    bindHostHeaderListeners();
    requestHostHeaderUpdate();
  }

  function initializeAppDownload(root) {
    const navigatorState = window.navigator || {};
    const userAgent = navigatorState.userAgent || '';
    const isIOS = /iPad|iPhone|iPod/i.test(userAgent)
      || (navigatorState.platform === 'MacIntel' && navigatorState.maxTouchPoints > 1);
    root.querySelectorAll('[data-ori-app-download]').forEach((link) => {
      const attribute = isIOS
        ? 'data-ori-app-download-ios'
        : 'data-ori-app-download-android';
      const href = link.getAttribute(attribute);
      if (href) link.setAttribute('href', href);
    });
  }

  function initializeRoot(root) {
    if (root.hasAttribute(enhancedAttribute)) return;
    root.setAttribute(enhancedAttribute, 'true');
    initializeFaq(root);
    initializeFaqCategories(root);
    root.querySelectorAll('[data-ori-carousel]').forEach(initializeCarousel);
    root.querySelectorAll('[data-ori-world-carousel]').forEach(initializeWorldCarousel);
    root.querySelectorAll('[data-ori-auto-rail]').forEach(initializeAutoRail);
    root.querySelectorAll('[data-ori-prepare-player]').forEach(initializePreparePlayer);
    root.querySelectorAll('[data-ori-motion-tabs]').forEach(initializeMotionTabs);
    root.querySelectorAll('[data-ori-flavor-tabs]').forEach(initializeFlavorTabs);
    root.querySelectorAll('[data-ori-spec-tabs]').forEach(initializeSpecificationTabs);
    initializeRevealGroups(root);
    initializeScreenFitting(root);
    initializeIntroReveal(root);
    initializeDetailsVideo(root);
    initializeMobileDetails(root);
    initializeFloatingNavigation(root);
    initializeHostHeaderVisibility(root);
    initializeAppDownload(root);
    root.addEventListener('click', (event) => handleRootClick(root, event));
  }

  function initializeRoots(scope) {
    if (scope && scope.matches && scope.matches(rootSelector)) initializeRoot(scope);
    if (scope && scope.querySelectorAll) scope.querySelectorAll(rootSelector).forEach(initializeRoot);
  }

  document.querySelectorAll('[data-ori-mix-root]').forEach(initializeRoot);

  if (!document.documentElement.hasAttribute(sectionListenerAttribute)) {
    document.documentElement.setAttribute(sectionListenerAttribute, 'true');
    document.addEventListener('shopify:section:load', (event) => initializeRoots(event.target));
  }
})();
